import { trapFocus, removeTrapFocus } from '@utilities/theme';

/**
 * @fileoverview Details Modal Component
 *
 * A custom element that enhances the native HTML `<details>` element with modal-like
 * behavior including focus trapping, body scroll locking, and click-outside-to-close
 * functionality.
 *
 * This component wraps a `<details>` element and converts it into a modal dialog
 * with proper accessibility support and focus management. It automatically finds
 * visible inputs to focus, which is critical for mobile keyboard appearance.
 *
 * @example
 * <details-modal class="header__search">
 *   <details>
 *     <summary aria-haspopup="dialog" aria-label="Search">
 *       <span class="modal__toggle-open">Open</span>
 *       <span class="modal__toggle-close">Close</span>
 *     </summary>
 *     <div class="modal__content" role="dialog" aria-modal="true">
 *       <div tabindex="-1">
 *         <input type="search" placeholder="Search...">
 *         <button type="button">Close</button>
 *       </div>
 *     </div>
 *   </details>
 * </details-modal>
 */

/**
 * @classdesc A custom element that provides modal behavior for `<details>` elements.
 *
 * Features:
 * - Focus trapping within the modal
 * - Body scroll locking when open
 * - Click-outside-to-close functionality
 * - Escape key to close
 * - Automatic focus on visible inputs (supports responsive designs)
 * - Proper ARIA support for accessibility
 *
 * @extends HTMLElement
 */
export class DetailsModal extends HTMLElement {
  /**
   * Creates a new DetailsModal instance.
   *
   * Sets up event listeners for:
   * - Escape key to close modal
   * - Summary click to toggle modal
   * - Inner toggle click (if present)
   * - Close button click
   *
   * @constructor
   */
  constructor() {
    super();

    /** @type {HTMLDetailsElement} The details element container */
    this.detailsContainer = this.querySelector('details');

    /** @type {HTMLElement} The summary element that triggers the modal */
    this.summaryToggle = this.querySelector('summary');

    /** @type {HTMLElement|null} Optional inner toggle element */
    this.innerToggle = this.querySelector('.modal__inner-toggle');

    /** @type {Function|null} Bound body click handler for cleanup */
    this.onBodyClickEvent = null;

    // Set up escape key handler
    this.detailsContainer.addEventListener(
      'keyup',
      (event) => event.code.toUpperCase() === 'ESCAPE' && this.close()
    );

    // Set up summary toggle
    this.summaryToggle.addEventListener(
      'click',
      this.onSummaryClick.bind(this)
    );

    // Set up optional inner toggle
    this.innerToggle?.addEventListener(
      'click',
      this.onSummaryClick.bind(this)
    );

    // Set up close button
    this.querySelector('button[type="button"]').addEventListener(
      'click',
      this.close.bind(this)
    );

    // Set summary role and initial expanded state for accessibility
    this.summaryToggle.setAttribute('role', 'button');
    this.summaryToggle.setAttribute('aria-expanded', 'false');
  }

  /**
   * Checks if the modal is currently open.
   *
   * @returns {boolean} True if the modal is open, false otherwise
   */
  isOpen() {
    return this.detailsContainer.hasAttribute('open');
  }

  /**
   * Handles click events on the summary element.
   *
   * Prevents default browser behavior and toggles the modal state.
   *
   * @param {MouseEvent} event - The click event
   * @private
   */
  onSummaryClick(event) {
    event.preventDefault();
    event.target.closest('details').hasAttribute('open')
      ? this.close()
      : this.open(event);
  }

  /**
   * Handles click events on the document body.
   *
   * Closes the modal if the click is outside the modal content
   * or on a modal overlay element.
   *
   * @param {MouseEvent} event - The click event
   * @private
   */
  onBodyClick(event) {
    if (!this.contains(event.target) || event.target.classList.contains('modal-overlay')) this.close(false);
  }

  /**
   * Opens the modal.
   *
   * This method:
   * 1. Sets the `open` attribute on the details element
   * 2. Adds body click listener for outside clicks
   * 3. Locks body scroll
   * 4. Finds a visible input to focus (important for mobile keyboard)
   * 5. Traps focus within the modal
   *
   * @param {MouseEvent} event - The triggering click event
   *
   * @fires trapFocus - Sets up focus trapping within the modal
   *
   * @example
   * // The modal is typically opened via summary click, but can be opened programmatically:
   * const modal = document.querySelector('details-modal');
   * modal.open({ target: modal.querySelector('summary') });
   */
  open(event) {
    this.onBodyClickEvent =
      this.onBodyClickEvent || this.onBodyClick.bind(this);
    event.target.closest('details').setAttribute('open', true);
    this.summaryToggle.setAttribute('aria-expanded', 'true');
    document.body.addEventListener('click', this.onBodyClickEvent);
    document.body.classList.add('overflow-hidden');

    // Find a visible input to focus (important for mobile keyboard to appear)
    // On mobile, the desktop input is hidden via CSS, so we need to find one that's actually visible
    const inputs = this.detailsContainer.querySelectorAll('input:not([type="hidden"])');
    const visibleInput = [...inputs].find(input => {
      // Check if the input or its ancestors are hidden
      // offsetParent is null for hidden elements (display: none)
      // But also check offsetWidth/offsetHeight for elements hidden other ways
      return input.offsetParent !== null || (input.offsetWidth > 0 && input.offsetHeight > 0);
    });

    // Trap on the whole <details> (not just the inner content div) so the
    // summary/toggle — which becomes the visible close (X) button once open —
    // is itself part of the cycle. It's a sibling of the content div, so a
    // trap scoped to just that div could never reach it via Tab.
    trapFocus(
      this.detailsContainer,
      visibleInput || inputs[0]
    );
  }

  /**
   * Closes the modal.
   *
   * This method:
   * 1. Removes focus trapping
   * 2. Removes the `open` attribute from the details element
   * 3. Removes body click listener
   * 4. Unlocks body scroll
   * 5. Optionally returns focus to the summary toggle
   *
   * @param {boolean} [focusToggle=true] - Whether to return focus to the summary toggle
   *
   * @fires removeTrapFocus - Removes focus trapping from the modal
   *
   * @example
   * // Close and return focus to toggle
   * modal.close();
   *
   * // Close without returning focus
   * modal.close(false);
   */
  close(focusToggle = true) {
    removeTrapFocus(focusToggle ? this.summaryToggle : null);
    this.detailsContainer.removeAttribute('open');
    this.summaryToggle.setAttribute('aria-expanded', 'false');
    document.body.removeEventListener('click', this.onBodyClickEvent);
    document.body.classList.remove('overflow-hidden');
  }
}

if (!customElements.get('details-modal')) {
  customElements.define('details-modal', DetailsModal);
}
